import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useReducer, useState, type FormEvent } from "react";
import { Logo } from "@/components/Logo";
import { OnboardingLifecycleContent } from "@/components/OnboardingLifecycleContent";
import { Button } from "@/components/ui/button";
import {
  getReadyGeneratedCustomer,
  assertPublicAcquisition,
  subscribeCustomerLifecycle,
} from "@/data/firestore/customerLifecycleRepository";
import { toAppError } from "@/data/errors";
import { BackendApiError } from "@/lib/backend-api";
import { pageTitle } from "@/lib/brand";
import { useI18n } from "@/lib/i18n";
import {
  readRememberedGeneratedCustomer,
  rememberGeneratedCustomer,
  clearRememberedGeneratedCustomer,
} from "@/onboarding/generated-customer";
import {
  dashboardRouteFor,
  discoveryLaunchFor,
  identityFromResult,
  initialOnboardingState,
  onboardingLifecycleReducer,
} from "@/onboarding/lifecycle";
import { runOnboarding } from "@/onboarding/runOnboarding";
import { useDiscoveryHost } from "@/components/discovery-host-context";

export const Route = createFileRoute("/onboarding")({
  head: () => ({ meta: [{ title: pageTitle("Create your Qotta") }] }),
  component: Onboarding,
});

function normalizeOnboardingWebsite(value: string): string | null {
  try {
    const url = new URL(
      /^https?:\/\//i.test(value.trim()) ? value.trim() : `https://${value.trim()}`,
    );
    return url.hostname.includes(".") ? url.toString() : null;
  } catch {
    return null;
  }
}

function Onboarding() {
  const { openDiscovery } = useDiscoveryHost();
  const navigate = useNavigate();
  const router = useRouter();
  const { t } = useI18n();
  const [url, setUrl] = useState(
    () => window.sessionStorage.getItem("qotta.previewWebsiteUrl") ?? "",
  );
  const [state, dispatch] = useReducer(onboardingLifecycleReducer, initialOnboardingState);

  const start = useCallback(async (websiteUrl: string) => {
    const normalizedDomain = new URL(websiteUrl).hostname.replace(/^www\./i, "");
    dispatch({ type: "request_started", websiteUrl, normalizedDomain });
    try {
      const result = await runOnboarding(websiteUrl);
      rememberGeneratedCustomer(identityFromResult(result));
      dispatch({ type: "request_resolved", result });
    } catch (caught) {
      const appError = toAppError(caught, "onboarding-failed");
      if (appError.cause === "protected-acquisition") clearRememberedGeneratedCustomer();
      const remembered = readRememberedGeneratedCustomer();
      const customer = remembered?.normalizedDomain === normalizedDomain ? remembered : undefined;
      dispatch({
        type: "failed",
        websiteUrl,
        normalizedDomain,
        ...(customer ? { customer } : {}),
        message: appError.cause === "protected-acquisition" ? t("acquisition.protected") : appError.message,
        code: caught instanceof BackendApiError ? caught.code : appError.code,
        requestId: caught instanceof BackendApiError ? caught.requestId : null,
      });
      // The backend owns analysis error persistence; retain browser diagnostics.
      console.error("[Qotta call initial website analysis backend]", appError.cause ?? appError);
    }
  }, [t]);

  useEffect(() => {
    if (state.mode !== "input") return;
    const websiteUrl = normalizeOnboardingWebsite(url);
    if (websiteUrl && window.sessionStorage.getItem("qotta.autoOnboarding") === "1") {
      window.sessionStorage.removeItem("qotta.autoOnboarding");
      void start(websiteUrl);
    }
  }, [start, state.mode, url]);

  useEffect(() => {
    if (state.mode !== "analyzing" || !state.customer) return;
    const knownCustomer = state.customer;
    let active = true;
    let resolvingReady = false;
    const unsubscribe = subscribeCustomerLifecycle(knownCustomer.customerId, {
      next: (lifecycle) => {
        if (!active) return;
        const customer = {
          ...knownCustomer,
          normalizedDomain: lifecycle.normalizedDomain || knownCustomer.normalizedDomain,
          websiteUrl: lifecycle.websiteUrl || knownCustomer.websiteUrl,
        };
        rememberGeneratedCustomer(customer);
        if (lifecycle.onboardingStatus === "analyzing") return;
        if (lifecycle.onboardingStatus === "not_relevant") {
          dispatch({
            type: "not_relevant_observed",
            customer,
            ...(lifecycle.qottaFitReason ? { reason: lifecycle.qottaFitReason } : {}),
          });
          return;
        }
        if (lifecycle.onboardingStatus === "failed") {
          dispatch({
            type: "failed",
            websiteUrl: customer.websiteUrl,
            normalizedDomain: customer.normalizedDomain,
            customer,
            message: t("onboarding.failed.body"),
            code: "onboarding_failed",
          });
          return;
        }
        if (resolvingReady) return;
        resolvingReady = true;
        void getReadyGeneratedCustomer(lifecycle)
          .then((readyCustomer) => {
            if (!active) return;
            rememberGeneratedCustomer(readyCustomer);
            dispatch({ type: "ready_observed", customer: readyCustomer });
          })
          .catch((caught) => {
            if (!active) return;
            const appError = toAppError(caught, "onboarding-failed");
            dispatch({
              type: "failed",
              websiteUrl: customer.websiteUrl,
              normalizedDomain: customer.normalizedDomain,
              customer,
              message: appError.message,
              code: appError.code,
            });
          });
      },
      error: (appError) => {
        if (!active) return;
        if (appError.cause === "protected-acquisition") clearRememberedGeneratedCustomer();
        dispatch({
          type: "failed",
          websiteUrl: knownCustomer.websiteUrl,
          normalizedDomain: knownCustomer.normalizedDomain,
          customer: knownCustomer,
          message: appError.cause === "protected-acquisition" ? t("acquisition.protected") : appError.message,
          code: appError.code,
        });
      },
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [state, t]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const websiteUrl = normalizeOnboardingWebsite(url);
    if (!websiteUrl) {
      dispatch({ type: "validation_failed", message: t("onboarding.input.error") });
      return;
    }
    window.sessionStorage.setItem("qotta.previewWebsiteUrl", websiteUrl);
    void start(websiteUrl);
  };

  const tryAnother = () => {
    window.sessionStorage.removeItem("qotta.previewWebsiteUrl");
    window.sessionStorage.removeItem("qotta.autoOnboarding");
    setUrl("");
    dispatch({ type: "reset" });
  };

  const retry = () => {
    if (state.mode !== "failed") return;
    void start(state.websiteUrl);
  };

  const openReadyExperience = async (destination: "discovery" | "dashboard") => {
    if (state.mode !== "ready") return;
    try {
      await assertPublicAcquisition(state.customer.customerId);
      if (destination === "discovery") openDiscovery(discoveryLaunchFor(state.customer));
      else void navigate(dashboardRouteFor(state.customer));
    } catch (caught) {
      const error = toAppError(caught, "onboarding-failed");
      if (error.cause === "protected-acquisition") clearRememberedGeneratedCustomer();
      dispatch({ type: "failed", websiteUrl: state.customer.websiteUrl, normalizedDomain: state.customer.normalizedDomain, message: error.cause === "protected-acquisition" ? t("acquisition.protected") : error.message });
    }
  };

  const experience = () => { void openReadyExperience("discovery"); };
  const openDashboard = () => { void openReadyExperience("dashboard"); };

  const expanded = state.mode === "ready";

  return (
    <main className={`hero-gradient grid min-h-screen place-items-center p-5 ${state.mode === "analyzing" ? "analysis-waiting-page" : ""}`}>
      <section
        className={`surface-elevated w-full p-7 transition-[max-width] duration-500 sm:p-10 ${
          state.mode === "analyzing" ? "analysis-waiting-panel max-w-6xl md:py-5" : expanded ? "max-w-2xl" : "max-w-xl"
        }`}
      >
        <div className="flex items-center justify-between gap-4">
          <Logo />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full border-primary/20 bg-card px-4 text-foreground shadow-sm hover:border-primary/35 hover:bg-primary-soft/60"
            onClick={() => {
              if (window.history.length > 1) router.history.back();
              else void navigate({ to: "/" });
            }}
          >
            <ArrowLeft className="text-primary rtl:rotate-180" aria-hidden />
            {t("common.back")}
          </Button>
        </div>

        <OnboardingLifecycleContent
          state={state}
          url={url}
          onUrlChange={setUrl}
          onSubmit={submit}
          onTryAnother={tryAnother}
          onRetry={retry}
          onExperience={experience}
          onDashboard={openDashboard}
        />
      </section>
    </main>
  );
}
